import AddToCartWithQuantitySelector from "~/components/product/AddToCartWithQuantitySelector"
import { formatPrice } from "@decocms/apps-commerce/sdk/formatPrice"
import { useOffer } from "~/sdk/useOffer"
import { useVariantPossibilities } from "@decocms/apps-commerce/sdk/useVariantPossibilities"
import type { Product } from "@decocms/apps-commerce/types"
import Image from "~/components/ui/Image"
import Link from "~/components/ui/Link"
import { SendEventOnClick } from "~/components/Analytics"
import clsx from "~/sdk/clx"
import getMostCheapSKU from "~/sdk/getMostCheapSKU"
import { getFinalPrice } from "~/sdk/price"
import { toAnalytics } from "~/sdk/ga4/transform/toAnalytics"
import { toProductItem } from "~/sdk/ga4/transform/toProductItem"
import { urlify } from "~/sdk/urlify"
import { useId } from "react"
import TimerJS from "~/components/ui/TimerJS"
import { MONTHLY_SKU_ID } from "~/sdk/useSubscription"
import WishlistButton from "~/components/wishlist/WishlistButton"

interface Props {
    /**
     * @title Produto
     */
    product: Product
    /**
     * @title Pré-carregar imagem
     */
    preload?: boolean
    /**
     * @title Nome da lista de itens
     * @description Usado para o evento de estatiscas
     */
    itemListName?: string
    itemListId?: string
    variant?: 'Countdown-home'
    index: number

    isMobile: boolean
    isPerformanceTestPage?: boolean
    disabledBuyMonthlySubscription?: boolean
}

const IMAGE_REDUCTION_FACTOR = 0.75
const IMAGE_WIDTH = 355 * IMAGE_REDUCTION_FACTOR
const MOBILE_IMAGE_WIDTH = 160 * IMAGE_REDUCTION_FACTOR
const IMAGE_HEIGHT = 355 * IMAGE_REDUCTION_FACTOR
const MOBILE_IMAGE_HEIGHT = 160 * IMAGE_REDUCTION_FACTOR

function ProductCard(
    {
        product,
        preload,
        itemListId,
        itemListName,
        isMobile,
        variant,
        index,
        isPerformanceTestPage = false,
        disabledBuyMonthlySubscription,
    }: Props,
) {
    const mostCheapSKU = getMostCheapSKU(product.isVariantOf?.hasVariant ?? []) ?? product

    const { isVariantOf, inProductGroupWithID } = product
    const { url, productID, name = '', image: images, offers } = mostCheapSKU

    const id = `product-card-${productID}`
    const hasVariant = isVariantOf?.hasVariant ?? []
    const [front] = images ?? []
    const availableOffer = offers?.offers[0]?.availability === 'https://schema.org/InStock'
        ? offers
        : isVariantOf?.hasVariant?.find((v) =>
            v.offers?.offers[0]?.availability === 'https://schema.org/InStock'
        )
            ?.offers
    const { listPrice = 0, price = 0, installments, seller = '1' } = useOffer(availableOffer)
    const possibilities = useVariantPossibilities(hasVariant, product)
    const variantLength = Object.keys(possibilities).length

    const hasOnlyOneVariant = Object.values(possibilities).reduce(
        (acc, curr) => acc + Object.keys(curr).length,
        0,
    ) === variantLength

    const hasVariants = (variantLength > 1 ||
        (variantLength === 1 && Object.keys(Object.values(possibilities)[0]).length > 1)) &&
        !hasOnlyOneVariant

    const rawProductUrl = isVariantOf?.url ?? url ?? '#'
    // Normalize to a same-origin path so TanStack Router can intercept the click.
    // VTEX returns mixed shapes — some absolute (https://www.miess.com.br/x/p),
    // some pathless (kit-foo/p), some relative. new URL() with a base handles all.
    const productUrl = rawProductUrl === '#' ? '#' : (() => {
        try {
            const u = new URL(rawProductUrl, 'https://placeholder.local/')
            return `${u.pathname}${u.search}`
        } catch {
            return rawProductUrl.startsWith('/') ? rawProductUrl : `/${rawProductUrl}`
        }
    })()
    const isAvailable = !!availableOffer

    const teaser = offers?.offers[0]?.teasers?.find((teaser) =>
        teaser.effects.parameters.some((parameter) => parameter.name === 'PercentualDiscount')
    )

    const teaserLabel = teaser?.name
        .split('-')[0]
        .trim()
        .replace(/(\d+%)/, '$1 OFF')
        .replace(/pix/i, 'PIX')

    const currency = offers?.priceCurrency
    const vitrineVira = images?.find((image) => image.name?.toLowerCase() === 'vira')
    const isVariacaoImage = images?.find((image) => image.name?.toLowerCase() === 'variacao')
    const { finalPrice, discountType } = getFinalPrice(mostCheapSKU.offers)
    const isPriceZero = finalPrice === 0

    const timerId = useId()

    const countdown = product.isVariantOf
        ?.additionalProperty.find(({ name }) => name === 'CountDown')
        ?.value

    const countdownExpired = new Date(countdown ?? 0).getTime() < new Date().getTime()

    const analytics = {
        items: [toProductItem(product, { index, quantity: 1 })],
        view: { id: itemListId ?? 'product_card', name: itemListName ?? 'Produto' },
    }

    const selectItemEvent = toAnalytics({
        type: 'select_item',
        data: analytics,
    })

    // Ajustar no codigo depois dos testes, colocar essa funcao como utilitario
    const bankersRound = (num: number, decimalPlaces: number) => {
        const d = decimalPlaces || 0
        const m = Math.pow(10, d)
        const n = +(d ? num * m : num).toFixed(8) // Avoid rounding errors
        const i = Math.floor(n), f = n - i
        const e = 1e-8 // Allow for rounding errors in f
        const r = (f > 0.5 - e && f < 0.5 + e) ? ((i % 2 === 0) ? i : i + 1) : Math.round(n)
        return d ? r / m : r
    }

    return (
        <div
            id={id}
            className={clsx(
                'flex flex-col h-full gap-3 w-full pb-px m-auto',
                // layout?.variant === 'big' ? '' : 'max-w-[314px]',
            )}
            data-deco='view-product'
        >
            <figure className='overflow-hidden shrink-0 relative isolate'>
                {/* Product Images */}
                <Link
                    href={productUrl}
                    title={name}
                    aria-label='Ver produto'
                    className='w-full flex items-center justify-center overflow-hidden border group/image border-neutral-200 relative'
                >
                    {/* <div className='absolute top-2 right-2 z-10'>
                        <WishlistButton variant='icon' productID={productID} sku={product.sku} />
                    </div> */}
                    <Image
                        src={isVariacaoImage ? isVariacaoImage.url! : front.url!}
                        alt={name}
                        width={isMobile ? MOBILE_IMAGE_WIDTH : IMAGE_WIDTH}
                        height={isMobile ? MOBILE_IMAGE_HEIGHT : IMAGE_HEIGHT}
                        className='w-full bg-neutral-200 duration-500 transition-all lg:group-hover/image:scale-[1.20] aspect-square'
                        sizes='(max-width: 640px) 50vw, 20vw'
                        preload={preload}
                        loading={preload ? 'eager' : 'lazy'}
                        decoding='async'
                    />
                    {vitrineVira && (
                        <Image
                            src={vitrineVira.url ?? ''}
                            alt={name}
                            width={isMobile ? MOBILE_IMAGE_WIDTH : IMAGE_WIDTH}
                            height={isMobile ? MOBILE_IMAGE_HEIGHT : IMAGE_HEIGHT}
                            className='bg-neutral-200 duration-500 transition-all absolute inset-0 opacity-0 lg:group-hover/image:opacity-100 lg:group-hover/image:scale-[1.20] w-full h-full'
                            sizes='(max-width: 640px) 50vw, 20vw'
                            loading='lazy'
                            decoding='async'
                        />
                    )}
                </Link>

                {/* Konfidency - selo Top Rated sobre a imagem. Mesmo tratamento
                    anti-hidratação do `konfidency-reviews-multi` abaixo. */}
                <div
                    className='konfidency-badges absolute bottom-1 right-1/2 translate-x-1/2 z-[1] pointer-events-none'
                    data-sku={inProductGroupWithID}
                    suppressHydrationWarning
                    dangerouslySetInnerHTML={{ __html: '' }}
                />
            </figure>
            {/* Prices & Name */}
            <div className='h-full flex flex-col flex-auto gap-3 text-center'>
                <Link href={productUrl}>
                    <h2
                        className='text-sm font-bold line-clamp-3 text-neutral-600 h-14'
                        dangerouslySetInnerHTML={{
                            __html: isVariantOf?.name ?? name,
                        }}
                    />
                </Link>
                <div className='flex flex-col items-center justify-center text-center gap-y-0.5'>
                    {isAvailable && teaserLabel && (
                        <div className='flex items-center justify-center text-xs text-white w-full md:w-[150px] min-h-[25px]'>
                            <p className='w-full h-full py-1 rounded-[5px] bg-[#361318] font-bold'>
                                {teaserLabel}
                            </p>
                        </div>
                    )}
                    {product.brand?.name && (
                        <h3 className='leading-none'>
                            <a
                                href={`/${urlify(product.brand.name)}?map=b`}
                                className={`text-xs font-bold text-neutral-600 ${
                                    (isAvailable && teaserLabel) ? '' : 'block pt-7'
                                }`}
                            >
                                {product.brand.name}
                            </a>
                        </h3>
                    )}
                </div>
                <div className='justify-center text-center h-7 hidden' />

                {/* Konfidency - estrelas nas listagens (data-sku = Product ID) */}
                <div className='trustvox-showcase'>
                    {/* Konfidency (Vue 3rd-party) injeta filhos + classe `loaded`/`data-v-app`
                        neste container antes da hidratação. dangerouslySetInnerHTML vazio faz
                        o React tratar o conteúdo como opaco (não reconcilia os filhos
                        injetados) e suppressHydrationWarning cobre os atributos — sem isso
                        dá "Hydration failed" (DECO-5454). */}
                    <div
                        className='konfidency-reviews-multi min-h-[20px]'
                        data-sku={inProductGroupWithID}
                        suppressHydrationWarning
                        dangerouslySetInnerHTML={{ __html: '' }}
                    />
                </div>

                {isPriceZero ? null : isAvailable
                    ? (
                        <>
                            <div
                                className='mt-auto flex flex-col items-center justify-end gap-2'
                                style={{
                                    containerName: 'card-buttons',
                                    containerType: 'inline-size',
                                }}
                            >
                                <a href={productUrl} className='flex flex-col'>
                                    <div className='flex flex-col justify-center gap-0'>
                                        {listPrice !== price && (
                                            <div className='font-semibold text-xs line-through text-neutral-400'>
                                                De {formatPrice(listPrice, offers?.priceCurrency)}
                                            </div>
                                        )}
                                        <p className='text-lg leading-none font-bold text-salmon'>
                                            {formatPrice(bankersRound(finalPrice, 2), currency)}
                                            {' '}
                                            {discountType === 'pix' && (
                                                <span className='text-lg font-normal'>no pix</span>
                                            )}
                                        </p>
                                    </div>
                                    <div className='min-h-[16px]'>
                                        {installments && (
                                            <div className='font-medium text-xs text-neutral-500'>
                                                <p
                                                    dangerouslySetInnerHTML={{
                                                        __html: installments,
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className='min-h-[16px]'>
                                        {isAvailable && (
                                            <p className='font-medium text-sm text-neutral-500'>
                                                <span className='font-bold'>
                                                    {formatPrice(price, offers?.priceCurrency)}
                                                </span>{' '}
                                                a prazo
                                            </p>
                                        )}
                                    </div>
                                </a>
                                {(hasVariants || (isPerformanceTestPage && isMobile))
                                    ? (
                                        <div className='flex justify-end w-full flex-col h-11'>
                                            <a
                                                href={productUrl}
                                                className='font-bold text-white border-0 rounded btn btn-block text-base bg-red-wine lg:hover:bg-wine h-full min-h-full focus:!bg-salmon-600'
                                            >
                                                COMPRAR
                                            </a>
                                        </div>
                                    )
                                    : (
                                        <AddToCartWithQuantitySelector
                                            isMobile={isMobile}
                                            product={{
                                                id: productID,
                                                quantity: 1,
                                                seller,
                                            }}
                                            inventoryLevel={product.offers?.offers[0]
                                                ?.inventoryLevel?.value ?? 0}
                                            layout={variant}
                                            analytics={analytics}
                                            disabledBuyMonthlySubscription={product.productID ===
                                                    MONTHLY_SKU_ID
                                                ? disabledBuyMonthlySubscription
                                                : false}
                                        />
                                    )}
                            </div>
                        </>
                    )
                    // `mt-auto` igual ao bloco de preço/compra do card disponível: sem
                    // ele o texto fica colado nas estrelas e toda a sobra de altura do
                    // card (o carrossel estica os itens até o card mais alto) vira um
                    // vão vazio no rodapé, empurrando o espaçamento até a próxima
                    // section.
                    : <p className='mt-auto text-xs font-semibold text-red-wine'>Produto Esgotado</p>}
            </div>

            {!countdownExpired && (
                <div>
                    <span className='p-1.5 mx-auto leading-none bg-orange text-red-wine font-black text-sm rounded-lg block mb-1 w-fit'>
                        OFERTA EXPIRA EM:
                    </span>

                    <div className='flex justify-center gap-0.5'>
                        <span className='p-2 text-white font-bold text-sm rounded countdown bg-red-wine'>
                            <span id={`${timerId}::days`} />
                        </span>
                        <span className='p-2 text-white font-bold text-sm rounded countdown bg-red-wine'>
                            <span id={`${timerId}::hours`} />
                        </span>
                        <span className='p-2 text-white font-bold text-sm rounded countdown bg-red-wine'>
                            <span id={`${timerId}::minutes`} />
                        </span>
                        <span className='p-2 text-white font-bold text-sm rounded countdown bg-red-wine'>
                            <span id={`${timerId}::seconds`} />
                        </span>
                    </div>
                </div>
            )}

            {!countdownExpired && <TimerJS rootId={timerId} expiresAt={countdown!} />}

            <SendEventOnClick id={id} event={selectItemEvent} />
        </div>
    )
}

export function LoadingFallback({ className }: { className?: string }) {
    return (
        <div className={clsx('w-full first:ml-auto last:mr-auto', className)} aria-hidden='true'>
            <div className='flex flex-col gap-3 pb-px m-auto w-full'>
                <div className='w-full aspect-square bg-gray-200' />
                <div className='w-full h-full flex flex-col flex-auto gap-3 text-center'>
                    <div className='card-title h-14 w-3/4 bg-gray-300 rounded-lg mx-auto'></div>
                    <div className='w-full flex flex-col gap-2'>
                        <div className='h-4 w-1/2 bg-gray-300 rounded-lg mx-auto'></div>
                        <div className='h-4 w-1/2 bg-gray-300 rounded-lg mx-auto'></div>
                    </div>
                    <div className='flex flex-col justify-between items-center w-lg mt-4 gap-1'>
                        <div className='h-4 w-20 bg-gray-300 rounded-lg'></div>
                        <div className='h-7 w-24 bg-gray-300 rounded-lg'></div>
                        <div className='h-4 w-32 bg-gray-300 rounded-lg'></div>
                        <div className='h-5 w-32 bg-gray-300 rounded-lg'></div>
                    </div>
                    <div className='w-full mt-2'>
                        {/* Retângulo de loading, não controle: como <button> vazio ele
                            entrava no accessibility tree sem nome (45 nós de
                            `button-name` na home). `btn-disabled` repõe o estilo que
                            vinha do seletor `:disabled`, que não se aplica a <div>. */}
                        <div
                            className='btn btn-primary btn-disabled w-full h-11 bg-gray-300'
                            aria-hidden='true'
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ProductCard
